(() => {
  class OtfCartCheckoutBar extends HTMLElement {
    connectedCallback() {
      this.checkout = document.getElementById('checkout');
      if (!this.checkout) return;
      this.observer = new IntersectionObserver(entries => {
        this.hidden = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 1);
      }, { threshold: [0, 1], rootMargin: '0px 0px -64px 0px' });
      this.observer.observe(this.checkout);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  if (!customElements.get('otf-cart-checkout-bar')) customElements.define('otf-cart-checkout-bar', OtfCartCheckoutBar);
  const pickupCache = new Map();
  class OtfCartDelivery extends HTMLElement {
    async connectedCallback() {
      const label = this.querySelector('[data-pickup-label]');
      if (!label) return;
      try {
        const variants = JSON.parse(this.dataset.pickupVariants || '[]');
        if (!variants.length || variants.length > 10) return;
        const availability = await Promise.all(variants.map(async item => {
          if (item.quantity !== 1) return false;
          const cached = pickupCache.get(item.id);
          if (cached && Date.now() - cached.time < 30000) return cached.available;
          const url = `${this.dataset.root}variants/${item.id}/?section_id=pickup-availability`;
          const response = await fetch(url, { signal: AbortSignal.timeout(8000) });
          if (!response.ok) return false;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const available = !!doc.querySelector('[data-otf-location="On Time Fashions Birmingham"][data-otf-pickup="true"]');
          pickupCache.set(item.id, { time: Date.now(), available });
          return available;
        }));
        if (this.isConnected && availability.every(Boolean)) label.textContent = this.dataset.pickupAvailable;
      } catch {}
    }
  }
  if (!customElements.get('otf-cart-delivery')) customElements.define('otf-cart-delivery', OtfCartDelivery);
  let frame;
  let observedChat, chatRoot, runtimeRoot, observedActivator, chatHydrationTimer;
  let chatHydrationAttempts = 0;
  const chatObserver = new MutationObserver(() => schedule());
  const chatResizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => schedule()) : null;
  const getChatActivator = () => {
    const chat = document.querySelector('shopify-chat');
    const nextChatRoot = chat?.shadowRoot;
    const nextRuntimeRoot = nextChatRoot?.querySelector('shopify-agent-runtime')?.shadowRoot;
    if (chat !== observedChat || nextChatRoot !== chatRoot || nextRuntimeRoot !== runtimeRoot) {
      chatObserver.disconnect();
      [nextChatRoot, nextRuntimeRoot].filter(Boolean).forEach(root => {
        chatObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded', 'part'] });
      });
      chatRoot = nextChatRoot;
      runtimeRoot = nextRuntimeRoot;
      observedChat = chat;
      chatHydrationAttempts = 0;
    }
    const activator = runtimeRoot?.querySelector('[part~="activator"]') || chatRoot?.querySelector('[part~="activator"]');
    if (activator !== observedActivator) {
      chatResizeObserver?.disconnect();
      if (activator) chatResizeObserver?.observe(activator);
      observedActivator = activator;
    }
    // Shadow-root attachment is not a DOM mutation. Bound retries to 30 seconds per root;
    // custom-element definition, DOM changes, scrolling and resizing still recheck later.
    if (chat && !activator && !chatHydrationTimer && chatHydrationAttempts < 60) {
      chatHydrationAttempts += 1;
      chatHydrationTimer = setTimeout(() => { chatHydrationTimer = null; schedule(); }, 500);
    } else if ((!chat || activator) && chatHydrationTimer) {
      clearTimeout(chatHydrationTimer);
      chatHydrationTimer = null;
    }
    return activator;
  };
  const synchronize = () => {
    frame = null;
    // Share geometry only within this pass; scroll, resize and DOM updates always remeasure.
    const rectangles = new Map();
    const measure = element => {
      if (!rectangles.has(element)) rectangles.set(element, element.getBoundingClientRect());
      return rectangles.get(element);
    };
    const cartActive = document.body.classList.contains('template-cart') || ['cart', 'quick-add-modal'].includes(document.body.getAttribute('active-popup'));
    if (cartActive) {
      document.querySelectorAll('[role="dialog"][class*="kl-private-reset-css"]').forEach(dialog => {
        if (dialog.style.display !== 'none') dialog.querySelector('button.klaviyo-close-form')?.click();
      });
    }
    const activator = getChatActivator();
    const shoppingOverlay = ['mobile-navigation', 'search'].includes(document.body.getAttribute('active-popup')) || !!document.querySelector('.mobile-facets__disclosure[open], .otf-size-guide-modal[open]');
    const chatClosed = activator?.getAttribute('aria-expanded') === 'false';
    let obstructs = shoppingOverlay && chatClosed;
    if (chatClosed && !cartActive && !shoppingOverlay) {
      const bubble = measure(activator);
      const viewport = { left: 0, top: 0, right: document.documentElement.clientWidth || window.innerWidth, bottom: window.innerHeight || document.documentElement.clientHeight };
      const ancestors = new Map();
      const intersects = box => box.right > box.left && box.bottom > box.top && box.top < bubble.bottom + 8 && box.bottom > bubble.top - 8 && box.left < bubble.right + 8 && box.right > bubble.left - 8;
      const visibleOverlap = target => {
        const rect = measure(target);
        const box = { left: Math.max(rect.left, viewport.left), top: Math.max(rect.top, viewport.top), right: Math.min(rect.right, viewport.right), bottom: Math.min(rect.bottom, viewport.bottom) };
        if (!intersects(box)) return false;
        const style = getComputedStyle(target);
        if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
        for (let parent = target.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
          let clip = ancestors.get(parent);
          if (!clip) {
            const parentStyle = getComputedStyle(parent);
            const x = /^(auto|scroll|hidden|clip)$/.test(parentStyle.overflowX), y = /^(auto|scroll|hidden|clip)$/.test(parentStyle.overflowY);
            clip = { x, y, rect: x || y ? measure(parent) : null };
            ancestors.set(parent, clip);
          }
          if (clip.x) { box.left = Math.max(box.left, clip.rect.left); box.right = Math.min(box.right, clip.rect.right); }
          if (clip.y) { box.top = Math.max(box.top, clip.rect.top); box.bottom = Math.min(box.bottom, clip.rect.bottom); }
          if (!intersects(box)) return false;
        }
        return true;
      };
      obstructs = Array.from(document.querySelectorAll('.otf-peachtree-preview__price, .otf-peachtree-preview__selection, .otf-peachtree-preview__confirm, .otf-peachtree-preview__cta, .otf-peachtree-preview__note, .otf-peachtree-preview__status, .otf-peachtree-preview__recovery, .otf-peachtree-preview__assistance, .otf-peachtree-preview__view-cart, .otf-peachtree-preview__terms > summary, .otf-peachtree-preview__terms[open] li, .otf-peachtree-preview__terms[open] > p, .otf-product-reassurance, .otf-product-selection, .otf-product-fit, pickup-availability, .quick-add__submit, .otf-card-purchase, .product-card-wrapper .price, .product-card-wrapper .card__heading, .product-form__buttons, .product-form__input, .product__info-container .price, .product-form__quantity, .quantity, .cart__ctas, .shopify-payment-button, .mobile-facets__footer, .otf-product-details, main h1, main h2, .otf-story-card__title, .otf-story-card__summary, .otf-story-card__kicker, .otf-context-shop, [data-story-video-controls], .template-index [data-story-previous], .template-index [data-story-next], .template-index slider-component button.slider-button--prev, .template-index slider-component button.slider-button--next, .banner__buttons, .otf-rotation__actions, .otf-rotation__more, .otf-rotation__product, .otf-astra__card, .otf-home-editorial .image-with-text__content .btn, .otf-home-editorial .image-with-text__text, main .field, footer .newsletter-form .field, footer .btn-newsletter__submit, footer a, footer .footer__block-toggle, footer .footer-block__heading, .otf-category-shelf__actions, .otf-category-shelf__browse, .otf-category-shelf__panel-heading a, .otf-pro-standard__panel-heading a, [data-category-tab], .otf-refinement__choice, .otf-collection-choices a, .otf-new-era-compact-browse__top a, main .contact .button, main .contact-form .button, .pagination__item, [data-video-toggle], [data-video-fallback], select[name="sort_by"], .collection-list .card__heading')).some(target => {
        return visibleOverlap(target);
      });
    }
    const teaser = document.querySelector('[class*="kl-teaser-"]');
    const teaserBox = teaser ? measure(teaser) : null;
    const signupObstructs = teaserBox && Array.from(document.querySelectorAll('.otf-product-reassurance, .otf-product-selection, .otf-product-fit, pickup-availability, .quick-add__submit, .otf-card-purchase, .product-card-wrapper .price, .product-form__buttons, .product-form__input, .product__info-container .price, .product-form__quantity, .quantity, .shopify-payment-button, .mobile-facets__footer, .otf-product-details, main h1, main h2, .banner__buttons, .otf-rotation__actions, .otf-rotation__more, .otf-rotation__product, .otf-astra__card, .otf-home-editorial .image-with-text__content .btn, .otf-home-editorial .image-with-text__text, main .field, footer .newsletter-form .field, footer .btn-newsletter__submit, footer a, footer .footer__block-toggle, footer .footer-block__heading, .otf-category-shelf__actions, .otf-category-shelf__browse, .otf-category-shelf__panel-heading a, main .contact .button, main .contact-form .button')).some(target => {
      const box = measure(target);
      return box.width > 0 && box.height > 0 && box.top < teaserBox.bottom + 8 && box.bottom > teaserBox.top - 8 && box.left < teaserBox.right + 8 && box.right > teaserBox.left - 8;
    });
    // Apply obstruction state after both collision checks, without a write between reads.
    document.body.classList.toggle('otf-chat-obstructs', obstructs);
    document.body.classList.toggle('otf-signup-obstructs', !!signupObstructs);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(synchronize); };
  ['shopify-chat', 'shopify-agent-runtime'].forEach(name => {
    customElements.whenDefined(name).then(() => { chatHydrationAttempts = 0; schedule(); });
  });
  new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['active-popup'] });
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  document.addEventListener('toggle', schedule, true);
  document.addEventListener('focusin', schedule);
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout' && document.querySelector('.cart__items--disabled')) event.preventDefault();
  }, true);
  schedule();
})();
